import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from '@tanstack/react-router'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ClubMembershipProvider } from './contexts/ClubMembershipContext'
import { useClubMembership } from './hooks/useClubMembership'
import { MEMBERSHIP_BILLING_ENABLED } from './lib/membership'
import { AppShell } from './components/layout/AppShell'
import { RedirectOnce } from './components/RedirectOnce'
import { withStandingRedirect } from './components/StandingRedirect'
import { Button, EmptyState, Spinner, ToastProvider } from './components/ui'
import { GolfLandingScreen } from './screens/GolfLandingScreen'
import { TheClubScreen } from './screens/TheClubScreen'
import { MembershipScreen } from './screens/MembershipScreen'
import { JoinApplyScreen } from './screens/JoinApplyScreen'
import { JoinConfirmScreen } from './screens/JoinConfirmScreen'
import { JoinCheckoutScreen } from './screens/JoinCheckoutScreen'
import { JoinWelcomeScreen } from './screens/JoinWelcomeScreen'
import { SignInScreen } from './screens/SignInScreen'
import { ClubhouseScreen } from './screens/ClubhouseScreen'
import { YourGameScreen } from './screens/YourGameScreen'
import { ClubWalletScreen } from './screens/ClubWalletScreen'
import { TheGroundsScreen } from './screens/TheGroundsScreen'
import { TheRegisterScreen } from './screens/TheRegisterScreen'
import { TheLoungeScreen } from './screens/TheLoungeScreen'
import { TheRecordScreen } from './screens/TheRecordScreen'
import { OutingsScreen } from './screens/OutingsScreen'
import { InviteScreen } from './screens/InviteScreen'
import { QuartermasterInviteScreen } from './screens/QuartermasterInviteScreen'
import { RoundDetailScreen } from './screens/RoundDetailScreen'
import { SharedRoundScreen } from './screens/SharedRoundScreen'
import { MemberAccountScreen } from './screens/MemberAccountScreen'
import { MemberProfileScreen } from './screens/MemberProfileScreen'
import { LandingScreen } from './screens/LandingScreen'
import { GroupsScreen } from './screens/GroupsScreen'
import { GroupDetailScreen } from './screens/GroupDetailScreen'
import { PlayScreen } from './screens/PlayScreen'
import { SessionResultsScreen } from './screens/SessionResultsScreen'
import { ProfileScreen } from './screens/ProfileScreen'
import { LibraryScreen } from './screens/LibraryScreen'
import { WalletScreen } from './screens/WalletScreen'
import { LEGACY_PRODUCT_NAME } from './lib/product'

/**
 * Fireside web app router. Code-based (no generated routeTree) so the engine
 * ships a self-contained SPA without a codegen step. The route set is
 * deliberate — there is NO catch-all (engine convention: unmatched paths must
 * cascade to the host app; golf paths are served by `pages#home`, steam SPA
 * paths by `pages#steam_home`).
 *
 * Route map (mirrors config/routes.rb; build-contract §4). The four marked
 * ‡ are the SELLING pages: a member in standing is redirected off them
 * (withStandingRedirect) — to the clubhouse, or for /membership to the
 * account page, which is where a member's own terms live.
 *   /                        → club homepage ‡ (→ /clubhouse)
 *   /the-club                → the charter, the course, the book
 *   /membership              → terms, privileges matrix, FAQ ‡ (→ /clubhouse/account)
 *   /join                    → the application (step I; carries the same card) ‡ (→ /clubhouse)
 *   /join/confirm            → sign-in confirmation (where the emailed link lands)
 *   /join/checkout           → settlement of dues ‡ (→ /clubhouse)
 *   /join/welcome            → arrival (Stripe success_url)
 *   /sign-in                 → the members' entrance (magic-link sign-in)
 *   /clubhouse               → the clubhouse (where the confirming POST lands)
 *   /clubhouse/grounds       → the grounds
 *   /clubhouse/register      → the register
 *   /clubhouse/lounge        → the lounge
 *   /clubhouse/outings       → the outings desk (tee times)
 *   /clubhouse/wallet        → the club wallet (balance + outing shares)
 *   /clubhouse/your-game     → your private strokes-gained ledger
 *   /clubhouse/account       → your account
 *   /clubhouse/rounds/:id    → one posted round, in full (data gated, door open)
 *   /members/:userId         → a member's profile (public; the OWNER's setting gates it)
 *   /invites/:token          → outing invitation (public, tokenized)
 *   /join/quartermaster/:token → quartermaster invite accept page (public, tokenized)
 *   /r/:token                → a round, shared (public, tokenized, noindexed shell)
 *   /steam                   → steam landing (hero + Steam OpenID return)
 *   /steam/groups            → groups list         (steam layout)
 *   /steam/groups/:id        → group detail        (steam layout)
 *   /steam/groups/:id/play   → play wizard         (steam layout)
 *   /steam/sessions/:id      → session results     (steam layout)
 *   /steam/profile           → profile             (steam layout)
 *   /steam/library           → library             (steam layout)
 *   /steam/wallet            → wallet + 3DS return (steam layout)
 */

// ── Layouts ──
const rootRoute = createRootRoute({
  component: () => (
    <AuthProvider>
      <ClubMembershipProvider>
        <ToastProvider>
          <Outlet />
        </ToastProvider>
      </ClubMembershipProvider>
    </AuthProvider>
  ),
})

/**
 * The club's MARKETING and join flow hang off the root and are public: they
 * are what someone reads before deciding to join, so gating them would remove
 * the sales surface entirely.
 *
 * The clubhouse is not. `/clubhouse/*` sits behind ClubhouseLayout, which
 * requires a standing in good order — see its own note for the three cases it
 * deliberately lets through.
 *
 * Each clubhouse screen still renders its full chrome for a visitor and swaps
 * only the genuinely personal region for a sign-in prompt (see SignInPrompt);
 * the gate is about PAYMENT, not about being signed in.
 *
 * `GolfLayout` is the shared loading gate: it holds the club behind a spinner
 * until the session resolves, so a signed-in member never sees a flash of the
 * visitor state.
 */
export function GolfLayout() {
  const { isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-paper">
        <Spinner size="lg" className="text-pine" />
      </div>
    )
  }
  return <Outlet />
}

// id (not path): pathless layout. 'golf-app' — layout ids must not collide
// with path route ids (see the 'steam-app' note below).
const golfLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'golf-app',
  component: GolfLayout,
})

/**
 * The one clubhouse page ANY signed-in member must always reach — lapsed,
 * unsettled, or brand new. It carries the payment fix and, more importantly,
 * the club's only Sign out control ("III. The Exit").
 */
const ACCOUNT_PATH = '/clubhouse/account'

/**
 * Statuses that mean the member never actually settled anything. An abandoned
 * bespoke checkout now leaves a real `incomplete` row behind, and that is NOT a
 * lapsed member — they hold nothing, so they belong in the join flow.
 */
const NEVER_SETTLED = ['incomplete', 'incomplete_expired']

/**
 * The clubhouse is for members in good standing (§12).
 *
 * Three deliberate refusals to over-gate:
 *
 * 1. A signed-out VISITOR still gets through. Every clubhouse screen already
 *    swaps its personal region for SignInPrompt and shows no member data, and
 *    the club is built to be looked over before anyone signs up. Bouncing
 *    someone we cannot yet say anything about would end that premise.
 * 2. While billing is dark nobody is held to a standing, because none exist.
 * 3. EVERY signed-in member keeps /clubhouse/account. For a lapsed member it is
 *    the page that fixes their payment; for everyone it is the only place the
 *    club offers Sign out. Gating it trapped a member who had just signed up:
 *    every clubhouse path came here, this page sent them to /join/checkout, and
 *    checkout carries no nav and no sign out — so on a shared machine the only
 *    ways out were paying or clearing cookies. Found in QA on a preview.
 *
 * A lapsed member is redirected to the account page, never to checkout: they
 * already hold a subscription, and sending them to buy a second one is the
 * wrong sentence and, in Stripe, the wrong action. A member with nothing
 * settled is still sent to checkout — that IS their next step — but never at
 * the cost of the exit.
 */
export function ClubhouseLayout() {
  const { isAuthenticated } = useAuth()
  const { membership, isLoading, failed } = useClubMembership()
  const { pathname } = useLocation()

  if (!isAuthenticated || !MEMBERSHIP_BILLING_ENABLED) return <Outlet />

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-paper">
        <Spinner size="lg" className="text-pine" />
      </div>
    )
  }

  if (membership?.active) return <Outlet />

  // Fail OPEN. `membership: null` below means "holds nothing" only when the
  // fetch actually answered; a member whose standing could not be loaded must
  // never be told to pay again. The API still enforces payment on every member
  // endpoint, so letting them through costs nothing but a possibly-empty page.
  if (failed) return <Outlet />

  // The exit is never gated, whatever the standing.
  if (pathname === ACCOUNT_PATH) return <Outlet />

  if (membership && !NEVER_SETTLED.includes(membership.status)) {
    return <Navigate to={ACCOUNT_PATH} replace />
  }

  // RedirectOnce, not Navigate: /join/checkout is OUTSIDE this layout, so the
  // commit that would unmount us keeps being cancelled by the next navigate,
  // and a plain Navigate re-fires on every re-render until React throws #185.
  // See RedirectOnce for the full mechanism. The ACCOUNT_PATH redirect above is
  // safe as-is — its target is inside this subtree, and the exit check above
  // (`pathname === ACCOUNT_PATH`) already acts as that branch's fixpoint.
  return <RedirectOnce to="/join/checkout" />
}

const clubhouseLayoutRoute = createRoute({
  getParentRoute: () => golfLayoutRoute,
  id: 'clubhouse',
  component: ClubhouseLayout,
})

/**
 * Public club routes, in the order of the route map above.
 *
 * Four are the SELLING pages — the homepage's join rail, the terms and dues,
 * the application, the checkout — and a member in standing is redirected off
 * them (withStandingRedirect): members in standing see no payment material.
 * The other four are left alone on purpose. /the-club is the charter, not a
 * sale; /join/confirm and /sign-in are how a member gets IN; and /join/welcome
 * is the post-payment landing, the one page a brand-new member must reach
 * while their standing flips to active. Nor can the checkout wrapper bounce
 * anyone mid-flow: JoinCheckoutScreen never refreshes the standing — its $0
 * path navigates straight to /join/welcome, and the card path does the same
 * from onConfirmed (or returns there through Stripe) — so the context still
 * reads "no standing" for the whole of the checkout and only welcome, which is
 * unwrapped, learns otherwise.
 */
const publicClubRoutes = [
  { path: '/', component: withStandingRedirect(GolfLandingScreen, '/clubhouse') },
  { path: '/the-club', component: TheClubScreen },
  { path: '/membership', component: withStandingRedirect(MembershipScreen, ACCOUNT_PATH) },
  { path: '/join', component: withStandingRedirect(JoinApplyScreen, '/clubhouse') },
  { path: '/join/confirm', component: JoinConfirmScreen },
  { path: '/join/checkout', component: withStandingRedirect(JoinCheckoutScreen, '/clubhouse') },
  { path: '/join/welcome', component: JoinWelcomeScreen },
  { path: '/sign-in', component: SignInScreen },
].map(({ path, component }) =>
  createRoute({ getParentRoute: () => golfLayoutRoute, path, component }),
)

/** Member-only routes, behind the standing gate. */
const clubhouseRoutes = [
  { path: '/clubhouse', component: ClubhouseScreen },
  { path: '/clubhouse/grounds', component: TheGroundsScreen },
  { path: '/clubhouse/register', component: TheRegisterScreen },
  { path: '/clubhouse/lounge', component: TheLoungeScreen },
  { path: '/clubhouse/record', component: TheRecordScreen },
  { path: '/clubhouse/outings', component: OutingsScreen },
  { path: '/clubhouse/wallet', component: ClubWalletScreen },
  { path: '/clubhouse/your-game', component: YourGameScreen },
  { path: ACCOUNT_PATH, component: MemberAccountScreen },
].map(({ path, component }) =>
  createRoute({ getParentRoute: () => clubhouseLayoutRoute, path, component }),
)

/**
 * Outing invitation — public and tokenized, reachable signed out. Hangs off
 * the golf layout for the club theme and the session-loading gate only; the
 * screen itself never requires auth (and must never sit behind the clubhouse
 * standing gate — invitees are not members).
 */
const inviteRoute = createRoute({
  getParentRoute: () => golfLayoutRoute,
  path: '/invites/$token',
  component: function InviteRoute() {
    const { token } = inviteRoute.useParams()
    return <InviteScreen token={token} />
  },
})

/**
 * Quartermaster invitation — public and tokenized exactly like the outing
 * invite above, and for the same reason NEVER behind the clubhouse standing
 * gate: the invitee has no standing yet; this page is where they get one.
 * It does require signing in to ACCEPT, but the screen carries its own
 * sign-in card rather than borrowing a gate that would hide the invitation.
 */
const quartermasterInviteRoute = createRoute({
  getParentRoute: () => golfLayoutRoute,
  path: '/join/quartermaster/$token',
  component: function QuartermasterInviteRoute() {
    const { token } = quartermasterInviteRoute.useParams()
    return <QuartermasterInviteScreen token={token} />
  },
})

/**
 * One posted round, in full — /clubhouse/rounds/:id. A param route, so it
 * cannot join the clubhouseRoutes array map; it still hangs off the clubhouse
 * layout for the member nav and the standing gate ON THE DATA (the shell is
 * indexable like every /clubhouse path — the API decides what a reader sees).
 */
const roundDetailRoute = createRoute({
  getParentRoute: () => clubhouseLayoutRoute,
  path: '/clubhouse/rounds/$roundId',
  component: function RoundDetailRoute() {
    const { roundId } = roundDetailRoute.useParams()
    return <RoundDetailScreen roundId={roundId} />
  },
})

/**
 * A round, shared — /r/:token. Public and tokenized exactly like the invites
 * above, and for the same reason NEVER behind the clubhouse standing gate:
 * the reader holds a link, not a membership. `?hole=N` is a HOLE NUMBER
 * (a back nine shares ?hole=14); anything unparseable degrades to null and
 * the screen shows the round from the top.
 */
const sharedRoundRoute = createRoute({
  getParentRoute: () => golfLayoutRoute,
  path: '/r/$token',
  component: function SharedRoundRoute() {
    const { token } = sharedRoundRoute.useParams()
    const search = sharedRoundRoute.useSearch() as Record<string, unknown>
    const holeRaw = Number(search.hole)
    const hole = Number.isInteger(holeRaw) && holeRaw >= 1 && holeRaw <= 18 ? holeRaw : null
    return <SharedRoundScreen token={token} hole={hole} />
  },
})

/**
 * A member's profile — deliberately OUTSIDE the clubhouse standing gate, for
 * the same reason the invite pages are: the audience is decided by the page's
 * subject, not by the reader's standing. A member who sets their profile
 * `public` is asking to be readable by someone with no account at all, and
 * `/clubhouse/*` would bounce exactly that reader (a lapsed member is
 * redirected to the account page there). The API applies the owner's own
 * setting per request and answers every refusal with one uniform 404 —
 * docs/fireside/member-profiles.md.
 */
const memberProfileRoute = createRoute({
  getParentRoute: () => golfLayoutRoute,
  path: '/members/$userId',
  component: function MemberProfileRoute() {
    const { userId } = memberProfileRoute.useParams()
    return <MemberProfileScreen userId={userId} />
  },
})

const clubRoutes = [
  ...publicClubRoutes,
  clubhouseLayoutRoute.addChildren([...clubhouseRoutes, roundDetailRoute]),
  inviteRoute,
  quartermasterInviteRoute,
  sharedRoundRoute,
  memberProfileRoute,
]

/** Steam landing (and Steam OpenID return). */
const steamLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/steam',
  component: LandingScreen,
})

/**
 * Steam shell — gates the legacy steam screens. Unauthenticated visitors go
 * to the steam landing; signed-in users without a Steam identity (golf
 * accounts) get an explicit steam sign-in prompt instead of the Outlet, since
 * every screen behind it assumes a Steam-linked user.
 */
export function SteamLayout() {
  const { isAuthenticated, isLoading, user, signInWithSteam } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <Spinner size="lg" />
      </div>
    )
  }
  if (!isAuthenticated) {
    // Same outside-the-subtree shape as the clubhouse gate: /steam is a sibling
    // under the root, not a child of this layout. It has not looped in practice
    // only because this layout does not subscribe to useLocation() — one
    // unmemoized context value away from doing so. Not worth leaving to luck.
    return <RedirectOnce to="/steam" />
  }
  if (!user?.steam_id) {
    return (
      <AppShell>
        <EmptyState
          title={`${LEGACY_PRODUCT_NAME} for Steam needs a Steam sign-in`}
          description={`This account isn't linked to Steam. Sign in through Steam to use the legacy ${LEGACY_PRODUCT_NAME} app.`}
          action={<Button onClick={signInWithSteam}>Sign in through Steam</Button>}
        />
      </AppShell>
    )
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}

// id (not path): pathless layout. 'steam-app' — a plain 'steam' id would
// collide with the /steam landing route's id.
const steamLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'steam-app',
  component: SteamLayout,
})

const groupsRoute = createRoute({
  getParentRoute: () => steamLayoutRoute,
  path: '/steam/groups',
  component: GroupsScreen,
})

const groupDetailRoute = createRoute({
  getParentRoute: () => steamLayoutRoute,
  path: '/steam/groups/$groupId',
  component: function GroupDetailRoute() {
    const { groupId } = groupDetailRoute.useParams()
    return <GroupDetailScreen groupId={groupId} />
  },
})

const groupPlayRoute = createRoute({
  getParentRoute: () => steamLayoutRoute,
  path: '/steam/groups/$groupId/play',
  component: function GroupPlayRoute() {
    const { groupId } = groupPlayRoute.useParams()
    return <PlayScreen groupId={groupId} />
  },
})

const sessionRoute = createRoute({
  getParentRoute: () => steamLayoutRoute,
  path: '/steam/sessions/$sessionId',
  component: function SessionRoute() {
    const { sessionId } = sessionRoute.useParams()
    return <SessionResultsScreen sessionId={sessionId} />
  },
})

const profileRoute = createRoute({
  getParentRoute: () => steamLayoutRoute,
  path: '/steam/profile',
  component: ProfileScreen,
})

const libraryRoute = createRoute({
  getParentRoute: () => steamLayoutRoute,
  path: '/steam/library',
  component: LibraryScreen,
})

/** Personal wallet — also the Stripe 3DS return_url landing (§3.7/§4). */
const walletRoute = createRoute({
  getParentRoute: () => steamLayoutRoute,
  path: '/steam/wallet',
  component: WalletScreen,
})

const routeTree = rootRoute.addChildren([
  golfLayoutRoute.addChildren(clubRoutes),
  steamLandingRoute,
  steamLayoutRoute.addChildren([
    groupsRoute,
    groupDetailRoute,
    groupPlayRoute,
    sessionRoute,
    profileRoute,
    libraryRoute,
    walletRoute,
  ]),
])

const router = createRouter({ routeTree, defaultPreload: 'intent' })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function App() {
  return <RouterProvider router={router} />
}
